import { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { FrogFace } from "@/components/FrogArt";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Loader2, XCircle, Home } from "lucide-react";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("polling"); // polling | paid | expired | failed | error
  const [record, setRecord] = useState(null);
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      return;
    }
    let cancelled = false;
    const tick = async () => {
      try {
        const r = await api.getPaymentStatus(sessionId);
        if (cancelled) return;
        setRecord(r);
        if (r.payment_status === "paid") return setStatus("paid");
        if (r.payment_status === "expired" || r.payment_status === "failed") return setStatus(r.payment_status);
        attempts.current += 1;
        if (attempts.current >= 20) return setStatus("error");
        setTimeout(tick, 2000);
      } catch {
        if (!cancelled) setStatus("error");
      }
    };
    tick();
    return () => { cancelled = true; };
  }, [sessionId]);

  const amount = record?.amount ? `$${(record.amount / 100).toFixed(2)}` : "your tip";

  return (
    <div className="min-h-screen bg-[#F0F9FF] flex items-center justify-center px-6">
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="max-w-md w-full bg-white rounded-3xl border border-slate-100 shadow-lg p-10 text-center space-y-4">
        {status === "polling" && (
          <>
            <Loader2 className="w-14 h-14 text-sky-500 mx-auto animate-spin" />
            <h1 data-testid="payment-polling" className="font-display text-2xl font-bold text-slate-800">Confirming your tip…</h1>
            <p className="text-slate-500 text-sm">Stripe is finalising things. Hang tight for a couple seconds.</p>
          </>
        )}
        {status === "paid" && (
          <>
            <motion.div animate={{ y: [0, -10, 0] }} transition={{ repeat: Infinity, duration: 1.8 }} className="inline-block">
              <FrogFace size={90} />
            </motion.div>
            <CheckCircle2 className="w-10 h-10 text-green-500 mx-auto" />
            <h1 data-testid="payment-paid" className="font-display text-3xl font-bold text-slate-800">
              Thank you for {amount}!
            </h1>
            <p className="text-slate-500">Your tip keeps the pond warm for other families. We appreciate you.</p>
            <Button data-testid="tip-back-home-btn" onClick={() => nav("/home")} className="btn-tactile h-12 rounded-xl bg-gradient-to-br from-sky-500 via-cyan-500 to-blue-500 hover:brightness-110 text-white font-display font-semibold">
              <Home className="w-4 h-4 mr-2" /> Back to the pond
            </Button>
          </>
        )}
        {(status === "expired" || status === "failed" || status === "error") && (
          <>
            <XCircle className="w-14 h-14 text-sky-500 mx-auto" />
            <h1 data-testid="payment-failed" className="font-display text-2xl font-bold text-slate-800">Tip didn't go through</h1>
            <p className="text-slate-500 text-sm">No charge was made. You can try again from the parent dashboard anytime.</p>
            <Button data-testid="tip-back-home-btn" onClick={() => nav("/home")} className="btn-tactile h-12 rounded-xl bg-white border-2 font-display font-semibold text-slate-700" variant="outline">
              <Home className="w-4 h-4 mr-2" /> Back to the pond
            </Button>
          </>
        )}
      </motion.div>
    </div>
  );
}
